import { z } from "zod/v4"
import { HomePickupId, SpaceKeySchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"

/**
 * ホームピックアップの種別
 * - space: スペースID指定
 * 
 * 将来的に以下の種別を追加予定:
 * - user: ユーザーID指定
 * - live-tag: LIVEタグ指定
 * - dress-code-tag: ドレスコードタグ指定
 */
export const HomePickupKind = z.enum(["space"])

export type HomePickupKind = z.infer<typeof HomePickupKind>

/**
 * スペースID指定のピックアップ
 */
export const HomePickupSpaceVariant = z.object({
  kind: z.literal("space").describe("スペースID指定"),
  space_id: SpaceKeySchema.describe("ピックアップ対象のスペースID"),
})

export type HomePickupSpaceVariant = z.infer<typeof HomePickupSpaceVariant>

/**
 * ホームピックアップのdiscriminated union
 * kindフィールドで種別を判別し、種別ごとに適切なプロパティを持つ
 */
export const HomePickupVariant = z.discriminatedUnion("kind", [
  HomePickupSpaceVariant,
])

export type HomePickupVariant = z.infer<typeof HomePickupVariant>

/**
 * ホームピックアップのドキュメント本体
 * ロジックB（ピックアップライブ）で表示対象とするスペース等を管理する
 */
export const HomePickupDocumentBody = z.intersection(
  HomePickupVariant,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type HomePickupDocumentBody = z.infer<typeof HomePickupDocumentBody>

export type HomePickupDocument = HomePickupDocumentBody & {
  id: HomePickupId,
}

export class HomePickupDocuments {

  static toDocumentData(target: HomePickupDocument): object {
    return {
      ...target,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): HomePickupDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const kind = data["kind"]
    const base = {
      id: HomePickupId(doc.id),
      kind,
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }

    switch (kind) {
      case "space":
        return {
          ...base,
          kind: "space" as const,
          space_id: data["space_id"],
        }
      default:
        return null
    }
  }
}

/**
 * ホームピックアップ作成リクエスト（スペースID指定）
 */
export const CreateHomePickupSpaceRequest = z.object({
  kind: z.literal("space").describe("スペースID指定"),
  spaceId: SpaceKeySchema.describe("ピックアップ対象のスペースID"),
})

export type CreateHomePickupSpaceRequest = z.infer<typeof CreateHomePickupSpaceRequest>

/**
 * ホームピックアップ作成リクエスト
 */
export const CreateHomePickupRequest = z.discriminatedUnion("kind", [
  CreateHomePickupSpaceRequest,
])

export type CreateHomePickupRequest = z.infer<typeof CreateHomePickupRequest>
